import { CrmShell } from '@/pages/crm/CrmShell';
import { isCrmHost } from '@/pages/crm/crm-access';
import { Outlet, Navigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { Button } from '@gokrew/ui';
import { useMediaQuery } from '@gokrew/ui/hooks';
import { useDialog } from '@gokrew/ui/context';
import { PageHeaderProvider } from '@/context/PageHeaderContext';
import { useAuth } from '@/context/AuthContext';
import { TopNav } from '@/components/TopNav';
import { Sidebar } from '@/components/Sidebar';
import { InitialResetPasswordPage } from '@/pages/auth/InitialResetPasswordPage';
import { cn } from '@gokrew/ui/lib/utils';
import styles from './MainLayout.module.css';

export function MainLayout() {
  const {
    isAuthenticated,
    isLoading,
    user,
    profileError,
    logout,
    refreshUser,
  } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const matches = useMediaQuery('(min-width: 64rem)');
  const { openDialog } = useDialog();
  const hasOpenedResetDialog = useRef(false);
  const { t } = useTranslation(TranslationNS_Admin.Auth);
  const { t: tc } = useTranslation('common');

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((collapsed) => !collapsed);
  }, []);

  const openSidebar = useCallback(() => {
    setIsSidebarCollapsed(false);
  }, []);

  useEffect(() => {
    setIsSidebarCollapsed(!matches);
  }, [matches]);

  useEffect(() => {
    if (
      user?.temporaryPasswordGeneratedAt &&
      user?.passwordChangedAt === null &&
      !hasOpenedResetDialog.current
    ) {
      hasOpenedResetDialog.current = true;

      openDialog({
        title: t('initial_reset_password.title'),
        hideHeader: true,
        containerClass: 'w-[480px] max-w-[480px]',
        disableClose: true,
        content: <InitialResetPasswordPage dialogMode />,
      });
    }
  }, [user, openDialog, t]);

  // ⏳ optional loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        {tc('loading_admin_panel')}
      </div>
    );
  }

  if (isAuthenticated && !user && profileError) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p>{tc('profile_error.message')}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refreshUser()}>
            {tc('profile_error.retry')}
          </Button>
          <Button variant="outline" onClick={() => logout()}>
            {tc('profile_error.logout')}
          </Button>
        </div>
      </div>
    );
  }

  if (isAuthenticated && !user) {
    return (
      <div className="h-screen flex items-center justify-center">
        {tc('loading_admin_panel')}
      </div>
    );
  }

  // 🔐 protect admin routes
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isCrmHost) return <CrmShell />;

  return (
    <PageHeaderProvider>
      <div className={styles.layout}>
        <Sidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />

        <div
          className={cn(styles.shell, isSidebarCollapsed && styles.collapsed)}
        >
          {/* Top Navigation */}
          <TopNav onMenuClick={openSidebar} isMenuOpen={!isSidebarCollapsed} />

          {/* Main Content */}
          <main className={styles.main}>
            <div className={styles.content}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </PageHeaderProvider>
  );
}
